import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { UGCReviewRow } from '@/common/model/ugcView'

import { fetchRest } from '@/common/api/fetcher'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const LABEL = 'text-secondary text-[11px] font-medium'
const FIELD = 'flex flex-col gap-1'

/** センチメント集計（backend/routers/ugc_voc.py の get_sentiment_summary） */
interface VocSentiment {
  total: number
  avg_rating: number
  distribution: Record<string, number>
  positive: number
  negative: number
  neutral: number
  positive_rate: number
  nps: number
  period_days: number
}

/** キーワード集計（同 get_keywords） */
interface VocKeywords {
  keywords: { word: string; count: number }[]
  total_reviews: number
  period_days: number
}

/** LP向けコピーの生成結果（同 generate-lp-copy） */
interface LpCopy {
  headline: string
  body: string
  key_message: string
}

export default function UGCVOCAnalysis() {
  const { siteId } = useParams()
  const [sentiment, setSentiment] = useState<VocSentiment | null>(null)
  const [keywords, setKeywords] = useState<VocKeywords | null>(null)
  const [reviews, setReviews] = useState<UGCReviewRow[]>([])
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState(30)
  const [tab, setTab] = useState('overview')
  const [generating, setGenerating] = useState(false)
  const [copies, setCopies] = useState<LpCopy[] | null>(null)
  const [persona, setPersona] = useState('')

  useEffect(() => {
    setLoading(true)
    Promise.all([
      fetchRest<VocSentiment>(`/api/sites/${siteId}/ugc/voc/sentiment?days=${days}`),
      fetchRest<VocKeywords>(`/api/sites/${siteId}/ugc/voc/keywords?days=${days}`),
      // 語彙統一で 'approved' は廃止（services/ugc_status.py）。公開中は 'published'
      fetchRest<{ items?: UGCReviewRow[] }>(
        `/api/sites/${siteId}/ugc/reviews?status=published&per_page=20`
      )
    ])
      .then(([s, k, r]) => {
        setSentiment(s)
        setKeywords(k)
        setReviews(r.items || [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId, days])

  const handleGenerateCopy = () => {
    setGenerating(true)
    setCopies(null)
    fetchRest<{ copies?: LpCopy[] }>(`/api/sites/${siteId}/ugc/voc/generate-lp-copy`, {
      method: 'POST',
      body: JSON.stringify({ target_persona: persona || null, copy_count: 3 })
    })
      .then((d) => setCopies(d.copies || []))
      .catch(() => setCopies([{ headline: 'エラー', body: '生成に失敗しました', key_message: '' }]))
      .finally(() => setGenerating(false))
  }

  const TABS = [
    { key: 'overview', label: 'センチメント' },
    { key: 'keywords', label: 'キーワード' },
    { key: 'lp-copy', label: 'LP向けコピー生成' },
    { key: 'reviews', label: '最新レビュー' }
  ]

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='💬 VOC/NPS分析'
        description='承認済みレビューからセンチメント・NPS・頻出キーワードを分析します'
        rightNode={
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={INPUT}>
            <option value={7}>7日間</option>
            <option value={30}>30日間</option>
            <option value={90}>90日間</option>
          </select>
        }
      />

      {/* タブ切替 */}
      <div className='mb-4 flex flex-wrap gap-1.5'>
        {TABS.map((t) => {
          const active = tab === t.key
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`cursor-pointer rounded-lg px-4 py-2 text-xs font-semibold ${
                active
                  ? 'bg-fill-brand text-on-bg-fill shadow-button-primary'
                  : 'bg-surface-secondary text-secondary hover:bg-surface-hover'
              }`}
            >
              {t.label}
            </button>
          )
        })}
      </div>

      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : (
        <>
          {/* センチメント */}
          {tab === 'overview' && sentiment && (
            <div>
              <div className='mb-4 grid grid-cols-2 gap-3 md:grid-cols-4'>
                <div className={`${CARD} px-4 py-3.5 text-center`}>
                  <div className='text-secondary text-[11px]'>レビュー数</div>
                  <div className='text-primary text-2xl font-bold'>{sentiment.total}</div>
                </div>
                <div className={`${CARD} px-4 py-3.5 text-center`}>
                  <div className='text-secondary text-[11px]'>平均評価</div>
                  <div className='text-warning text-2xl font-bold'>★ {sentiment.avg_rating}</div>
                </div>
                <div className={`${CARD} px-4 py-3.5 text-center`}>
                  <div className='text-secondary text-[11px]'>ポジティブ率</div>
                  <div className='text-success text-2xl font-bold'>{sentiment.positive_rate}%</div>
                </div>
                <div className={`${CARD} px-4 py-3.5 text-center`}>
                  <div className='text-secondary text-[11px]'>NPS</div>
                  <div
                    className={`text-2xl font-bold ${sentiment.nps >= 0 ? 'text-success' : 'text-critical'}`}
                  >
                    {sentiment.nps}
                  </div>
                </div>
              </div>

              <div className={`${CARD} p-5`}>
                <h3 className='text-primary m-0 text-sm font-bold'>評価分布</h3>
                <div className='mt-3 flex flex-col gap-1.5'>
                  {[5, 4, 3, 2, 1].map((star) => (
                    <div key={star} className='flex items-center gap-2'>
                      {/* 幅は★5個ぶんを確保する。w-8 では入りきらず2行に折り返していた */}
                      <span className='text-warning w-[5.5rem] shrink-0 text-[13px] whitespace-nowrap'>
                        {'★'.repeat(star)}
                      </span>
                      <div className='bg-surface-secondary h-3.5 flex-1 overflow-hidden rounded-full'>
                        <div
                          className={`h-full rounded-full ${star >= 4 ? 'bg-fill-success' : star === 3 ? 'bg-fill-warning' : 'bg-fill-critical'}`}
                          style={{
                            width: `${sentiment.total > 0 ? ((sentiment.distribution[star] || 0) / sentiment.total) * 100 : 0}%`
                          }}
                        />
                      </div>
                      <span className='text-secondary w-10 text-right text-xs font-semibold'>
                        {sentiment.distribution[star] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* キーワード */}
          {tab === 'keywords' && keywords && (
            <div className={`${CARD} p-5`}>
              <h3 className='text-primary m-0 text-sm font-bold'>
                頻出キーワード（{keywords.total_reviews}件のレビューから抽出）
              </h3>
              {keywords.keywords.length > 0 ? (
                <div className='mt-4 flex flex-wrap gap-2'>
                  {keywords.keywords.map((k) => (
                    <span
                      key={k.word}
                      className={`rounded-full px-3.5 py-1.5 ${
                        k.count >= 5
                          ? 'bg-surface-emphasis text-emphasis text-sm font-bold'
                          : k.count >= 3
                            ? 'bg-surface-warning text-warning text-xs font-semibold'
                            : 'bg-surface-secondary text-secondary text-[11px]'
                      }`}
                    >
                      {k.word} <span className='text-[10px] opacity-70'>({k.count})</span>
                    </span>
                  ))}
                </div>
              ) : (
                <p className='text-tertiary mt-3 text-[13px]'>キーワードデータが不足しています</p>
              )}
            </div>
          )}

          {/* LP向けコピー生成 */}
          {tab === 'lp-copy' && (
            <div className='flex flex-col gap-4'>
              <div className={`${CARD} p-5`}>
                <h3 className='text-primary m-0 text-sm font-bold'>AI コピー生成</h3>
                <p className='text-secondary mt-1.5 mb-3 text-xs'>
                  承認済みレビューからLP向けキャッチコピーをAIが自動生成します
                </p>
                <div className='flex items-end gap-2.5'>
                  <div className={`${FIELD} flex-1`}>
                    <label className={LABEL}>ターゲットペルソナ（任意）</label>
                    <input
                      value={persona}
                      onChange={(e) => setPersona(e.target.value)}
                      placeholder='例: 30代女性、乾燥肌に悩む人'
                      className={`${INPUT} w-full`}
                    />
                  </div>
                  <Button color={ButtonColor.PRIMARY} size='large' disabled={generating} onClick={handleGenerateCopy}>
                    {generating ? '生成中...' : '🤖 コピーを生成'}
                  </Button>
                </div>
              </div>

              {copies && (
                <div className='flex flex-col gap-3'>
                  {copies.map((c, i) => (
                    <div key={i} className={`${CARD} p-5`}>
                      <div className='text-emphasis mb-1.5 text-[10px] font-semibold'>
                        パターン {i + 1}
                      </div>
                      <h3 className='text-primary m-0 mb-2 text-lg font-bold'>{c.headline}</h3>
                      <p className='text-secondary m-0 mb-2 text-sm leading-relaxed'>{c.body}</p>
                      {c.key_message && (
                        <span className='bg-surface-emphasis text-emphasis rounded-lg px-2.5 py-1 text-[11px] font-semibold'>
                          訴求: {c.key_message}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 最新レビュー */}
          {tab === 'reviews' && (
            <div className={`${CARD} p-5`}>
              <h3 className='text-primary m-0 text-sm font-bold'>最新レビュー</h3>
              <div className='mt-3 flex flex-col gap-2.5'>
                {reviews.map((r) => (
                  <div key={r.id} className='bg-surface-secondary rounded-lg border border-secondary p-3'>
                    <div className='mb-1.5 flex items-center gap-2'>
                      <span className='text-primary min-w-0 truncate text-[13px] font-semibold'>
                        {r.reviewer_name || '匿名'}
                      </span>
                      {/* 名前が長いレビューでも星が折り返さないようにする */}
                      <span className='text-warning shrink-0 text-sm whitespace-nowrap'>
                        {'★'.repeat(r.rating || 0)}
                        {'☆'.repeat(5 - (r.rating || 0))}
                      </span>
                      {r.is_verified_purchase && (
                        <span className='bg-surface-success text-success rounded-lg px-1.5 py-0.5 text-[9px] font-semibold'>
                          購入確認済
                        </span>
                      )}
                      <span className='text-tertiary ml-auto text-[10px]'>
                        {r.created_at?.slice(0, 10)}
                      </span>
                    </div>
                    {r.title && (
                      <div className='text-secondary mb-1 text-xs font-semibold'>{r.title}</div>
                    )}
                    <p className='text-secondary m-0 text-xs leading-relaxed'>{r.body}</p>
                  </div>
                ))}
                {reviews.length === 0 && (
                  <p className='text-tertiary p-5 text-center text-[13px]'>レビューがありません</p>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </Main>
  )
}
